import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';;
import { ContractInformations } from '@core/models/contract-informations.model';
import { ContractItem } from '@core/models/contract-item.model';
import { ContractOtherCentralAgency } from '@core/models/contract-other-agency.model';
import { ContractUser } from '@core/models/contract-user.model';
import { Contract } from '@core/models/contract.model';
import { ContractCgvModelUpdateService } from '@core/services/contract-cgv-model-update/contract-cgv-model-update.service';
import { ContractGeneralInformationsService } from '@core/services/contract-general-information/contract-general-informations.service';
import { ContractOtherCentralAgenciesService } from '@core/services/contract-other-central-agencies/contract-other-central-agencies.service';
import { ContractUsersService } from '@core/services/contract-users/contract-users.service';
import { ContractService } from '@core/services/contract/contract.service';
import { ContractAdvertiserInformationsComponent } from '@shared/contract/contract-item/contract-general-informations/contract-advertiser-informations/contract-advertiser-informations.component';
import { ContractAdvertisingCenterInformationsComponent } from '@shared/contract/contract-item/contract-general-informations/contract-advertising-center-informations/contract-advertising-center-informations.component';
import { ContractInformationsComponent } from '@shared/contract/contract-item/contract-general-informations/contract-informations/contract-informations.component';
import { ContractManagerInformationsComponent } from '@shared/contract/contract-item/contract-general-informations/contract-manager-informations/contract-manager-informations.component';
import { ContractUtInformationsComponent } from '@shared/contract/contract-item/contract-general-informations/contract-ut-informations/contract-ut-informations.component';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
standalone: false,
  selector: 'app-contract-general-informations',
  templateUrl: './contract-general-informations.component.html',
  styleUrls: ['./contract-general-informations.component.scss'],
})
export class ContractGeneralInformationsComponent implements OnInit, AfterViewInit {
  @Input() contract: Contract;
  @Input() contractItem!: ContractItem;
  @Output() contractItemChange = new EventEmitter<ContractItem>();
  @Input() checkSignedFn!: (stateId: number | null | undefined) => boolean;

  @ViewChild(ContractAdvertiserInformationsComponent) contractAdvertiserInformationsComponent!: ContractAdvertiserInformationsComponent;
  @ViewChild(ContractManagerInformationsComponent) contractManagerInformationsComponent!: ContractManagerInformationsComponent;
  @ViewChild(ContractUtInformationsComponent) contractUtInformationsComponent!: ContractUtInformationsComponent;
  @ViewChild(ContractInformationsComponent) contractInformationsComponent!: ContractInformationsComponent;
  @ViewChild(ContractAdvertisingCenterInformationsComponent)
  contractAdvertisingCenterInformationsComponent!: ContractAdvertisingCenterInformationsComponent;

  contractItemInformations: ContractInformations;
  contractOtherCentralAgencyList: ContractOtherCentralAgency[] = [];
  contractUserList: ContractUser[] = [];

  formsValid = false;
  isSaving = false;
  isLoadingContractItem = false;

  componentDestroyed$: Subject<void> = new Subject();

  constructor(
    private contractGeneralInformationsService: ContractGeneralInformationsService,
    private contractUsersService: ContractUsersService,
    private contractOtherAgenciesService: ContractOtherCentralAgenciesService,
    private contractCgvModelUpdateService: ContractCgvModelUpdateService,
    public dialogRef: MatDialog,
    private snackBar: MatSnackBar,
    private contractService: ContractService
  ) {}

  ngOnInit(): void {
    this.contractItemInformations = this.contractItem?.contractInformations;
    this.contractOtherCentralAgencyList = [...this.contractItem?.contractOtherCentralAgencyList];
    this.contractUserList = [...this.contractItem?.contractUserList];
  }

  ngAfterViewInit() {
    this.isFormsValid();
    this.subscribeToChildrenForms();
  }

  private subscribeToChildrenForms() {
    this.contractAdvertiserInformationsComponent.advertiserGroupForm.valueChanges
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => this.isFormsValid());
    this.contractManagerInformationsComponent.contractManagerFormGroup.valueChanges
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => this.isFormsValid());
    this.contractUtInformationsComponent.contractUtFormGroup.valueChanges
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => this.isFormsValid());
    this.contractInformationsComponent.contractFormGroup.valueChanges
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => this.isFormsValid());
  }

  // --- Cancel ---

  public cancelFormsModification() {
    this.contractUserList = [...this.contractItem.contractUserList];
    this.contractOtherCentralAgencyList = [...this.contractItem.contractOtherCentralAgencyList];

    this.contractAdvertiserInformationsComponent.cancelFormsModification();
    this.contractManagerInformationsComponent.cancelFormsModification();
    this.contractUtInformationsComponent.cancelFormsModification();
    this.contractInformationsComponent.cancelFormsModification();
    this.contractAdvertisingCenterInformationsComponent.cancelFormsModification();
  }

  // --- Check Forms ---

  public isFormsValid() {
    Promise.resolve().then(() => {
      this.formsValid =
        this.contractAdvertiserInformationsComponent?.isFormValid() &&
        this.contractManagerInformationsComponent?.isFormValid() &&
        this.contractInformationsComponent?.isFormValid();
    });
  }

  // --- get data from forms ---

  public getFormsData() {
    this.isFormsValid();

    if (!this.formsValid) {
      return;
    }

    const contractAdvertiserData = this.contractAdvertiserInformationsComponent.getFormData();
    const contractManagerInformationsData = this.contractManagerInformationsComponent.getFormData();
    const contractUtInformationsData = this.contractUtInformationsComponent.getFormData();
    const contractInformationsData = this.contractInformationsComponent.getFormData();
    const contractAdvertisingCentersInformationsData = this.contractAdvertisingCenterInformationsComponent.getFormsData();

    return {
      advertiserData: contractAdvertiserData,
      contractManagerInformationsData: contractManagerInformationsData,
      usersData: contractUtInformationsData,
      contractInformationsData: contractInformationsData,
      otherCentralAgenciesData: contractAdvertisingCentersInformationsData,
    };
  }

  // --- Prepare data to save ---

  public getInformations(formsData) {
    return {
      id: this.contractItemInformations.id,
      contractId: this.contract.id,

      advertiserStatus: formsData.advertiserData.advertiserStatus,
      sector: formsData.advertiserData.sector,

      contractManager: formsData.contractManagerInformationsData.contractManager,
      customerServiceManager: formsData.contractManagerInformationsData.customerServiceManager,
      centralAgency: formsData.contractManagerInformationsData.centralAgency,

      contractType: formsData.contractInformationsData.contractType,
      salesServiceManager: formsData.contractInformationsData.salesServiceManager,
      billingManager: formsData.contractInformationsData.billingManager,
      sentOn: formsData.contractInformationsData.sentOn,
      customerReturnDate: formsData.contractInformationsData.customerReturnDate,
      state: formsData.contractInformationsData.contractState,
    };
  }

  private getContractUsers(formsData) {
    const result = [];

    formsData.usersData.forEach(contractUser =>
      result.push({
        id: null,
        contractId: this.contractItem.id,
        user: contractUser.user,
      })
    );

    return { contractId: this.contractItem.id, contractUsers: result };
  }

  private getCentralAgencies(formsData) {
    const result = [];

    formsData.otherCentralAgenciesData.forEach(centralAgency => {
      if (centralAgency) {
        result.push(centralAgency);
      }
    });

    return { contractId: this.contractItem.id, contractOtherCentralAgencies: result };
  }

  // --- Save all ---
  private markAllAsTouched() {
    this.contractAdvertiserInformationsComponent.markAllAsTouched();
    this.contractManagerInformationsComponent.markAllAsTouched();
    this.contractUtInformationsComponent.markAllAsTouched();
    this.contractInformationsComponent.markAllAsTouched();
    this.contractAdvertisingCenterInformationsComponent.markAllAsTouched();
  }

  public saveFormsModification() {
    this.isSaving = true;

    const formsData = this.getFormsData();

    if (!this.formsValid) {
      this.markAllAsTouched();
      this.isSaving = false;
      this.displaySnackBar('Erreur dans la saisie des informations. Veuillez vérifier le formulaire.', 'tv-chip-error');
      return;
    }

    const informationsDataToSave = this.getInformations(formsData);
    const contractUsersToSave = this.getContractUsers(formsData);
    const centralAgenciesDataToSave = this.getCentralAgencies(formsData);

    if (!informationsDataToSave.id || !contractUsersToSave.contractId || !centralAgenciesDataToSave.contractId) {
      this.isSaving = false;
      this.displaySnackBar('Erreur dans la saisie des informations. Veuillez vérifier le formulaire.', 'tv-chip-error');
      return;
    }

    forkJoin({
      informations: this.contractGeneralInformationsService.edit(informationsDataToSave),
      users: this.contractUsersService.edit(contractUsersToSave),
      centralAgencies: this.contractOtherAgenciesService.edit(centralAgenciesDataToSave),
    }).subscribe({
      next: result => {
        this.getContractItem({ searchElementById: this.contract.id, year: this.contractItem.year }); // to get the updated contractItem
      },
      error: err => {
        this.isSaving = false;
        this.displaySnackBar('Erreur lors de l’enregistrement.', 'tv-chip-error');
      },
    });
  }

  public getContractItem(filter) {
    this.isLoadingContractItem = true;

    this.contractService
      .getContractItem(filter)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        contractItem => {
          this.isLoadingContractItem = false;
          this.updateContractCgvModel();
          if (contractItem) {
            this.updateContractItem(contractItem);
          }
        },
        error => {
          console.log(error);
          this.isLoadingContractItem = false;
        }
      );
  }

  private updateContractCgvModel() {
    // Appel synchrone de la méthode parent fournie en @Input
    const isSigned = this.checkSignedFn ? this.checkSignedFn(this.contractItem.contractInformations.state.id) : false;

    this.contractCgvModelUpdateService.updateContractCgvModel(this.contractItem.id, this.contractItem).subscribe(
      response => {
        this.isSaving = false;
        if (isSigned) {
          this.displaySnackBar(
            'Les données ont bien été enregistrées. Vous avez effectué une modification sur un contrat signé.',
            'tv-chip-success'
          );
        } else {
          this.displaySnackBar('Les données ont bien été enregistrées.', 'tv-chip-success');
        }
      },
      error => {
        this.displaySnackBar("Erreur lors de la sauvegarde dans l'ancien modèle. Veuillez contacter le support.", 'tv-chip-error');
        this.isSaving = false;
      }
    );
  }

  // --- Utility ---

  updateContractItem(newContractItem) {
    this.contractItem = newContractItem;

    this.contractItemChange.emit(newContractItem);
  }

  public displaySnackBar(message: string, panelClass: string) {
    this.snackBar.open(message, null, {
      duration: 4000,
      verticalPosition: 'top',
      panelClass: [panelClass],
    });
  }

  // --- On Destroy ---

  ngOnDestroy() {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }
}
